import {
  rebuildSearchIndex,
  searchQuickTales,
  backfillListenCounts,
} from "../../../../firebase";

/**
 * Hooks for the in-house search-index backfill (AddSearch replacement), driven
 * from the GAQuickTales admin panel.
 *
 * `rebuildSearchIndex` is a paged Cloud Function: each call processes up to
 * `limit` docs and returns a `nextCursor`. These helpers drive that loop from
 * the client so the admin can watch progress, and one bad doc never aborts the
 * whole run.
 *
 * See docs/ADDSEARCH_REPLACEMENT_PLAN.md (Phase 1.6).
 */

/**
 * Run a single page of the backfill.
 * @param {Object} params
 * @param {boolean} [params.dryRun=false] - count only, no writes
 * @param {number} [params.limit=300] - docs per call
 * @param {Object|null} [params.cursor=null] - previous response's nextCursor
 * @param {'all'|'quicktale'|'album'} [params.docType='all']
 * @returns {Promise<{processed:number, byType:Object, dryRun:boolean, nextCursor:Object|null}>}
 */
export const runSearchIndexBackfillPage = async ({
  dryRun = false,
  limit = 300,
  cursor = null,
  docType = "all",
} = {}) => {
  const res = await rebuildSearchIndex({ dryRun, limit, cursor, docType });
  return res.data;
};

/**
 * Drive the full backfill loop until `nextCursor` is null (or `shouldStop`
 * returns true). Reports progress after each page via `onProgress`.
 *
 * @param {Object} params
 * @param {boolean} [params.dryRun=false]
 * @param {number} [params.limit=300]
 * @param {'all'|'quicktale'|'album'} [params.docType='all']
 * @param {(progress:{processed:number, pages:number, byType:Object, lastPage:Object}) => void} [params.onProgress]
 * @param {() => boolean} [params.shouldStop] - return true to abort after the current page
 * @returns {Promise<{processed:number, pages:number, byType:Object, stopped:boolean}>}
 */
export const runSearchIndexBackfill = async ({
  dryRun = false,
  limit = 300,
  docType = "all",
  onProgress,
  shouldStop,
} = {}) => {
  let cursor = null;
  let processed = 0;
  let pages = 0;
  const byType = {};
  let stopped = false;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const page = await runSearchIndexBackfillPage({ dryRun, limit, cursor, docType });
    pages += 1;
    processed += page.processed || 0;
    Object.entries(page.byType || {}).forEach(([k, v]) => {
      byType[k] = (byType[k] || 0) + (v || 0);
    });

    if (typeof onProgress === "function") {
      onProgress({ processed, pages, byType: { ...byType }, lastPage: page });
    }

    cursor = page.nextCursor || null;
    if (!cursor) break;
    if (typeof shouldStop === "function" && shouldStop()) {
      stopped = true;
      break;
    }
  }

  return { processed, pages, byType, stopped };
};

/**
 * Run the in-house search endpoint (Search Lab). Returns the raw payload:
 * { results, total, tookMs }.
 *
 * @param {Object} params
 * @param {string} params.q - query text
 * @param {'typeahead'|'full'} [params.mode='typeahead']
 * @param {number} [params.limit]
 * @param {'quicktale'|'album'} [params.docType] - optional filter
 * @param {Object} [params.configOverride] - what-if search config (not saved),
 *   e.g. { search: { fieldWeights: { title: 6 } } } from the Search Lab tuning UI
 * @returns {Promise<{results:object[], total:number, tookMs:number}>}
 */
export const runSearchQuery = async ({
  q,
  mode = "typeahead",
  limit,
  docType,
  configOverride,
} = {}) => {
  const payload = { q, mode };
  if (limit) payload.limit = limit;
  if (docType) payload.filters = { docType };
  if (configOverride) payload.configOverride = configOverride;
  const res = await searchQuickTales(payload);
  return res.data;
};

/**
 * Drive the two-phase listen-count backfill from userActivity `play_duration`
 * events:
 *   1) Aggregation — page the events, accumulating per-quicktale tallies in the
 *      client (count + total seconds).
 *   2) Apply — send the merged tallies once so the Cloud Function writes the
 *      absolute counters (SET, idempotent). Skipped when `dryRun` is true.
 *
 * The subsequent quicktale writes trigger onQuickTaleWriteIndex, which refreshes
 * searchIndex listenCount + popScore automatically.
 *
 * @param {Object} params
 * @param {boolean} [params.dryRun=false] - aggregate + report only, no writes
 * @param {number} [params.limit=2000] - events scanned per page
 * @param {(p:{pages:number, processed:number, counted:number, quicktales:number}) => void} [params.onProgress]
 * @param {() => boolean} [params.shouldStop]
 * @returns {Promise<{pages:number, processed:number, counted:number, quicktales:number, applied:number, missing:number, dryRun:boolean, stopped:boolean}>}
 */
export const runListenCountBackfill = async ({
  dryRun = false,
  limit = 2000,
  onProgress,
  shouldStop,
} = {}) => {
  const tallies = {};
  let cursor = null;
  let pages = 0;
  let processed = 0;
  let counted = 0;
  let stopped = false;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await backfillListenCounts({ limit, cursor });
    const page = res.data || {};
    pages += 1;
    processed += page.processed || 0;
    counted += page.counted || 0;

    Object.entries(page.pageTallies || {}).forEach(([qtId, t]) => {
      if (!tallies[qtId]) tallies[qtId] = { count: 0, seconds: 0 };
      tallies[qtId].count += t.count || 0;
      tallies[qtId].seconds += t.seconds || 0;
    });

    if (typeof onProgress === "function") {
      onProgress({
        pages,
        processed,
        counted,
        quicktales: Object.keys(tallies).length,
      });
    }

    cursor = page.nextCursor || null;
    if (!cursor) break;
    if (typeof shouldStop === "function" && shouldStop()) {
      stopped = true;
      break;
    }
  }

  const applyRes = await backfillListenCounts({ applyMode: true, tallies, dryRun });
  const apply = applyRes.data || {};

  return {
    pages,
    processed,
    counted,
    quicktales: Object.keys(tallies).length,
    applied: apply.applied || 0,
    missing: apply.missing || 0,
    dryRun,
    stopped,
  };
};
